import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Building2, BookOpen, Plus, Loader2, ShieldCheck, Send, StickyNote } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SEOHead } from '@/components/SEOHead';
import { useAdmin } from '@/hooks/useAdmin';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import logo from '@/assets/gaius-logo.webp';

interface Enrollment {
  id: string;
  training_title: string;
  modality: string;
  total_price: string;
  payment_status: string;
  created_at: string;
}

interface AdminNote {
  id: string;
  note: string;
  created_at: string;
}

const statusBadge = (s: string) => {
  switch (s) {
    case 'paid': return <Badge className="bg-emerald-100 text-emerald-800 text-xs">Payé</Badge>;
    case 'refunded': return <Badge className="bg-orange-100 text-orange-800 text-xs">Remboursé</Badge>;
    default: return <Badge className="bg-yellow-100 text-yellow-800 text-xs">En attente</Badge>;
  }
};

export const AdminClientPage = () => {
  const { email } = useParams<{ email: string }>();
  const decodedEmail = decodeURIComponent(email || '');
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [notes, setNotes] = useState<AdminNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!adminLoading && !authLoading && (!user || !isAdmin)) navigate('/');
  }, [isAdmin, adminLoading, authLoading, user, navigate]);

  useEffect(() => {
    if (!isAdmin || !decodedEmail) return;
    const fetchData = async () => {
      // Fetch enrollments for this client (by guest_email or user_id via profiles)
      const { data: allEnrollments } = await supabase
        .from('enrollments')
        .select('*')
        .order('created_at', { ascending: false });

      const clientEnrollments = (allEnrollments as any[] || []).filter(
        (e: any) => e.guest_email?.toLowerCase() === decodedEmail.toLowerCase()
      );

      // Also check by user_id if there's a profile with this email
      // For now, show guest enrollments by email
      setEnrollments(clientEnrollments);
      if (clientEnrollments.length > 0) {
        setClientName(clientEnrollments[0].guest_name || '');
      }

      // Fetch admin notes
      const { data: notesData } = await supabase
        .from('admin_notes' as any)
        .select('*')
        .eq('client_email', decodedEmail.toLowerCase())
        .order('created_at', { ascending: false });

      if (notesData) setNotes(notesData as any[]);

      setLoading(false);
    };
    fetchData();
  }, [isAdmin, decodedEmail]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    const { error } = await supabase
      .from('admin_notes' as any)
      .insert({ client_email: decodedEmail.toLowerCase(), note: newNote.trim(), created_by: user!.id } as any);
    setSavingNote(false);
    if (error) {
      toast({ variant: 'destructive', title: 'Erreur', description: error.message });
    } else {
      setNotes(prev => [{ id: crypto.randomUUID(), note: newNote.trim(), created_at: new Date().toISOString() }, ...prev]);
      setNewNote('');
      toast({ title: 'Note ajoutée' });
    }
  };

  if (adminLoading || authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  const totalSpent = enrollments.reduce((sum, e) => sum + (parseFloat(e.total_price.replace(/[^0-9.,]/g, '').replace(',', '.')) || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={`Client: ${clientName || decodedEmail} — Admin Gaius`} description="Fiche client admin" />

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src={logo} alt="Gaius" className="h-10" />
              </Link>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <h1 className="text-xl font-bold text-foreground">Fiche client</h1>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Client info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              {clientName || 'Client'}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span>{decodedEmail}</span>
            </div>
            {clientCompany && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span>{clientCompany}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <BookOpen className="w-4 h-4 text-muted-foreground" />
              <span>{enrollments.length} formation(s) — {totalSpent.toFixed(0)} € total</span>
            </div>
          </CardContent>
        </Card>

        {/* Enrollments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Formations inscrites</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Formation</TableHead>
                  <TableHead>Modalité</TableHead>
                  <TableHead>Prix</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrollments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Aucune inscription</TableCell>
                  </TableRow>
                ) : (
                  enrollments.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.training_title}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{e.modality}</Badge>
                      </TableCell>
                      <TableCell>{e.total_price}</TableCell>
                      <TableCell>{statusBadge(e.payment_status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(e.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Notes privées */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <StickyNote className="w-5 h-5 text-primary" />
              Notes privées
            </CardTitle>
            <CardDescription>Notes internes visibles uniquement par les administrateurs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Textarea
                placeholder="Ajouter une note..."
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                className="flex-1"
                rows={2}
              />
              <Button onClick={handleAddNote} disabled={savingNote || !newNote.trim()}>
                {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune note</p>
            ) : (
              <div className="space-y-3">
                {notes.map(n => (
                  <div key={n.id} className="p-3 rounded-lg bg-muted/50 border border-border">
                    <p className="text-sm text-foreground whitespace-pre-wrap">{n.note}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open(`mailto:${decodedEmail}`, '_blank')}>
            <Send className="w-4 h-4 mr-1" /> Envoyer un email
          </Button>
        </div>
      </main>
    </div>
  );
};
